import { useTranslation } from 'next-i18next'
// Dependencies.
import styled from 'styled-components'
import { useState, useContext, useEffect } from 'react'

import { colors, icons } from '../utils'
import { Modal } from './Modal'
import { CTA } from './CTA'
import { H3 } from './H3'
import { P } from './P'
import { UserContext } from '../context'
import { TimezoneSelect } from './Inputs/TimezoneSelect'
import { FormField } from './Inputs/FormField'

const Div = styled.div`
  width: 575px;
  max-width: 100%;
  height: 594px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${colors.white};
  overflow: visible;

  .modalHeader {
    width: 405px;
    height: 87px;
    max-width: 100%;
    margin-bottom: 20px;
  }

  .modalContent {
    width: 405px;
    height: 63px;
    max-width: 100%;
    margin-bottom: 30px;
    text-align: center;
  }

  .timezoneContainer {
    width: 90px;
    height: 90px;
    margin-top: 25px;
    margin-bottom: 30px;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .button {
    width: 263px;

    &.authorizeButton {
      margin-bottom: 70px;
    }
  }

  .errorContainer {
    margin-top: 8px;
  }

  .inputError {
    border-color: ${colors.red};
    opacity: 0.6;
  }

  .errorMessage {
    color: ${colors.red};
  }

  .form {
    width: 405px;
    max-width: 100%;
    padding-bottom: 45px;
  }
`

// Props.
interface Props {
  close: () => void
  header: string
  content: string
}

interface UserDetailError {
  time_zone: string[]
}

// Update user timezone
export const TimezoneUpdateModal: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  const { close, header, content } = props

  // Context.
  const { currentUser, fetchCurrentUser, updateProfileDetail } =
    useContext(UserContext)

  // State.
  const [loading, setLoading] = useState(false)
  const [timezone, setTimezone] = useState('')
  const [formError, setFormError] = useState<UserDetailError>({
    time_zone: [],
  })

  const background: React.CSSProperties = {
    background: `${colors.white}`,
    width: '575px',
    maxWidth: '100%',
    height: '594px',
    left: '50%',
    top: '50%',
    position: 'fixed',
    transform: 'translate(-50%, -50%)',
    overflow: 'visible',
    borderRadius: '10px',
    boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.1)',
  }

  const validateInput = (): boolean => {
    let result = true
    const currentFormError = { ...formError }

    if (!timezone || timezone === '') {
      result = false
      currentFormError.time_zone = ['Required *']
    } else {
      currentFormError.time_zone = []
    }

    setFormError(currentFormError)
    return result
  }

  const resetError = (): UserDetailError => {
    return { time_zone: [] }
  }

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent Default.
    e.preventDefault()

    if (validateInput()) {
      // Flag Loading.
      setLoading(true)
      await updateProfileDetail({ time_zone: timezone })
        .then((res) => {
          if (res === true) {
            close()
          } else {
            setFormError({ time_zone: ['Error updating time zone'] })
            setLoading(false)
          }
        })
        .catch((error) => {
          setFormError({ ...resetError(), ...error.response.data })
          setLoading(false)
        })
      //.finally(()=>{setLoading(false)})
    }
  }

  const timeZoneChange = ({ value }) => {
    setTimezone(value)

    const error = { ...formError }
    error['time_zone'] = []
    setFormError(error)
  }

  useEffect(() => {
    if (currentUser) {
      setTimezone(currentUser.time_zone)
    }
  }, [currentUser])

  useEffect(() => {
    if (!currentUser) {
      fetchCurrentUser()
    }
    return () => {
      setLoading(false)
    }
  }, [])

  // ..
  return (
    <Modal
      style={background}
      close={close}
      retainPointerEvents={true}
      zIndex={9999}>
      <Div className="modalContainer">
        <div className="timezoneContainer">
          <img src={icons.timezone} className="timezoneImage"></img>
        </div>
        <H3 className="modalHeader">{header}</H3>
        <P className="detail modalContent">{content}</P>
        {/* Timezone Select */}
        <FormField
          className="form"
          required
          title={t(`TimezoneUpdateModal348598599`, `Location (time zone)`)}>
          <TimezoneSelect
            onChange={timeZoneChange}
            selectedTimezone={timezone}
          />
          {formError.time_zone.length > 0 && (
            <div className="errorContainer">
              {formError.time_zone.map((error, index) => {
                return (
                  <P key={index} className="detail errorMessage">
                    {error}
                  </P>
                )
              })}
            </div>
          )}
        </FormField>
        <CTA
          disabled={loading}
          className="purple button authorizeButton"
          onClick={onSubmit}>
          {t(`Update`, `Update`)}
        </CTA>
      </Div>
    </Modal>
  )
}
